import { isValidRequestId } from "@k8s-dashboard/contracts";
import type {
  ResourceApplyResponse,
  ResourceDeletePropagation,
  ResourceDeleteResponse,
  ResourceDetailResponse,
  ResourceOperationResponse,
} from "@k8s-dashboard/contracts";
import { HttpError, apiRequest, clusterPath } from "@/api/client";
import { fixedTextFor } from "./dryrun";

/**
 * 변경 적용·삭제 전송 계층 (ADR 0019 Phase 2)
 * --------------------------------------------------------------------------
 * 검토 전송(dryrun.ts)과 같은 규칙 위에 **두 가지가 더** 있습니다.
 *
 * 1. **자격(token)은 저장하지 않습니다.** 검토 응답에서 받아 컴포넌트 상태로만
 *    들고 있다가 적용 요청 본문에 한 번 싣고 버립니다. URL·localStorage·
 *    sessionStorage·쿼리 캐시·console 어디에도 쓰지 않습니다. 매니페스트와 같은
 *    취급입니다.
 * 2. **제출 이후에는 취소가 없습니다.** 적용·삭제 요청에는 AbortSignal을 붙이지
 *    않습니다. 붙이면 사용자가 탭을 닫거나 화면을 벗어난 순간 요청이 끊기고, UI는
 *    그것을 "취소됨"으로 보여 주게 됩니다 — 그런데 서버는 이미 적용했을 수
 *    있습니다. 대신 결과를 못 받으면 requestId로 작업 상태를 다시 물어봅니다.
 * 3. **상관 ID는 보내기 전에 만듭니다.** 응답에서 받은 값에만 의존하면, 진짜
 *    네트워크 단절(응답 자체가 없음)에서 물어볼 ID가 없습니다 — 그때가 바로 결과를
 *    가장 알고 싶은 순간입니다. 그래서 제출 전에 만들어 `X-Request-ID`로 보내고
 *    로컬에 들고 있습니다. 서버는 안전한 형식일 때만 그 값을 재사용하고, 응답
 *    헤더·본문·감사 로그가 모두 같은 값을 가리킵니다.
 */

/**
 * 요청 하나의 상관 ID입니다.
 *
 * 서버가 재사용을 허용하는 문자 집합(`[A-Za-z0-9._:-]`, 128자 이하) 안에 있고,
 * 작업 조회 경로가 받는 형식(`[A-Za-z0-9-]`, 64자 이하)에도 맞아야 합니다.
 * UUID v4는 둘 다 만족합니다. `crypto.randomUUID`가 없는 환경(비보안 컨텍스트·
 * 구형 브라우저)에서는 `crypto.getRandomValues`로 같은 형식을 만듭니다.
 *
 * **저장하지 않습니다.** 컴포넌트 상태로만 살아 있고 언마운트와 함께 사라집니다.
 */
export function newCorrelationId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === "function") {
    c.getRandomValues(bytes);
  } else {
    /* 예측 가능한 값으로 조용히 물러나지 않도록 시간·난수를 함께 씁니다.
       이 경로는 상관 ID에만 쓰이고 보안 결정에는 쓰이지 않습니다. */
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = (Math.random() * 256) ^ (Date.now() >>> (i % 24));
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** 적용·삭제·작업 조회의 정본 경로입니다. 세그먼트를 모두 인코딩합니다. */
type ResourceRef = Pick<ResourceDetailResponse, "clusterId" | "group" | "version" | "resource">;

function resourceBase(ref: ResourceRef): string {
  const cluster = encodeURIComponent(ref.clusterId);
  const group = encodeURIComponent(ref.group);
  const version = encodeURIComponent(ref.version);
  const resource = encodeURIComponent(ref.resource);
  return `/api/v1/clusters/${cluster}/resources/${group}/${version}/${resource}`;
}

export function applyPath(ref: ResourceRef): string {
  return `${resourceBase(ref)}/object/apply`;
}

export function deletePath(ref: ResourceRef): string {
  return `${resourceBase(ref)}/object/delete`;
}

export function operationPath(clusterId: string, requestId: string): string {
  return clusterPath(clusterId, "resources", "operations", requestId);
}

/**
 * 적용 요청 하나입니다.
 *
 * 본문은 자격과 확인 이름 **둘뿐**입니다. 신원도 매니페스트도 보내지 않습니다 —
 * 서버가 자격으로 대상을 찾으므로, 화면이 다른 것을 적용시킬 방법이 없습니다.
 *
 * **signal이 없습니다.** 이것이 계약입니다.
 */
export function submitApply(args: {
  detail: ResourceRef;
  token: string;
  confirmName: string;
  /** 제출 **전에** 만든 상관 ID. 응답을 못 받아도 이 값으로 결과를 물어봅니다. */
  requestId: string;
}): Promise<ResourceApplyResponse> {
  return apiRequest<ResourceApplyResponse>(applyPath(args.detail), {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Request-ID": args.requestId },
    body: JSON.stringify({ token: args.token, confirmName: args.confirmName }),
  });
}

/** 삭제 요청 하나입니다. force·gracePeriodSeconds는 계약에 없고 여기서도 만들지 않습니다. */
export function submitDelete(args: {
  detail: ResourceDetailResponse;
  confirmName: string;
  propagationPolicy: ResourceDeletePropagation;
  /** 제출 **전에** 만든 상관 ID. */
  requestId: string;
}): Promise<ResourceDeleteResponse> {
  const { detail, confirmName, propagationPolicy } = args;
  return apiRequest<ResourceDeleteResponse>(deletePath(detail), {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Request-ID": args.requestId },
    body: JSON.stringify({
      ...(detail.namespace ? { namespace: detail.namespace } : {}),
      name: detail.name,
      uid: detail.uid,
      resourceVersion: detail.resourceVersion,
      confirmName,
      propagationPolicy,
    }),
  });
}

/**
 * 결과를 못 받았을 때 **실제로 무슨 일이 일어났는지** 물어봅니다. (ADR 0019 결정 6)
 *
 * 이 호출에는 signal을 붙여도 됩니다 — 조회는 되돌릴 것이 없습니다.
 */
export function fetchOperation(args: {
  clusterId: string;
  requestId: string;
  signal?: AbortSignal;
}): Promise<ResourceOperationResponse> {
  return apiRequest<ResourceOperationResponse>(operationPath(args.clusterId, args.requestId), {
    signal: args.signal,
  });
}

/**
 * 서버 오류 본문에서 requestId를 꺼냅니다. 응답을 받았을 때의 정본 값입니다.
 *
 * 판정은 **계약이 소유합니다**(`isValidRequestId`). 여기서 따로 좁히면 서버가
 * 정당하게 재사용한 ID(`req:abc` 같은 기존 추적 시스템 형식)를 화면이 버리고,
 * 미리 만든 값으로 되돌아가 실제 기록과 다른 ID를 물어보게 됩니다.
 */
export function requestIdOf(error: unknown): string | undefined {
  if (error instanceof HttpError && isValidRequestId(error.body.requestId)) {
    return error.body.requestId;
  }
  return undefined;
}

/**
 * 적용·삭제 오류 code만 고정 한국어로 옮깁니다.
 *
 * **`HttpError.body.message`를 쓰지 않습니다.** 검토와 같은 이유입니다 — 서버
 * 문자열을 DOM에 넣는 경로를 만들어 두면 어느 시점의 어떤 upstream 문자열이 화면에
 * 뜰지 UI가 보장할 수 없게 됩니다.
 */
const CHANGE_MESSAGES: Record<string, string> = {
  /* 자격 */
  change_token_invalid: "적용 자격이 만료되었거나 이미 사용되었습니다. 다시 검토하세요.",
  change_token_mismatch: "적용 자격이 지금의 대상·권한과 맞지 않습니다. 다시 검토하세요.",
  change_confirmation_mismatch: "확인을 위해 입력한 이름이 대상과 다릅니다.",

  /* 정책·권한 */
  change_unavailable: "이 배포에서는 변경 적용을 사용할 수 없습니다.",
  change_resource_denied: "이 리소스는 변경 적용 대상이 아닙니다.",
  change_forbidden: "서버에 이 리소스의 변경 권한이 없습니다. 관리자에게 문의하세요.",
  change_rate_limited: "변경 요청 한도를 초과했습니다. 잠시 후 다시 시도하세요.",
  invalid_propagation_policy: "지원하지 않는 삭제 전파 정책입니다.",
  namespace_access_denied: "이 Namespace에 대한 권한이 없습니다.",
  cluster_scope_required: "클러스터 범위 리소스는 클러스터 전체 권한이 필요합니다.",

  /* 신원·동시성 */
  resource_version_mismatch: "객체가 그 사이에 바뀌었습니다. 다시 조회한 뒤 검토하세요.",
  uid_mismatch: "같은 이름의 다른 객체로 교체되었습니다. 목록을 새로고침하세요.",
  not_found: "대상을 찾을 수 없습니다. 목록을 새로고침하세요.",

  /* 요청 형식 */
  bad_request: "요청 형식이 올바르지 않습니다. 화면을 새로고침한 뒤 다시 시도하세요.",
  unsupported_media_type: "요청 형식이 올바르지 않습니다. 화면을 새로고침한 뒤 다시 시도하세요.",
  request_too_large: "요청이 서버 상한을 넘었습니다.",
  invalid_filter: "요청 값이 서버 상한을 벗어났습니다.",

  /* upstream */
  upstream_unavailable: "클러스터가 변경을 끝내지 못했습니다.",
  upstream_timeout: "클러스터 응답 시간이 초과되었습니다.",
  operation_not_found: "작업 기록을 찾을 수 없습니다. 보존 기간이 지났을 수 있습니다.",
};

/** 알 수 없는 실패에 쓰는 유일한 문구입니다. */
export const CHANGE_FALLBACK_MESSAGE = "요청을 처리하지 못했습니다. 잠시 후 다시 시도하세요.";

/**
 * **결과를 모른다**는 사실을 그대로 말하는 문구입니다.
 *
 * "취소되었습니다"도 "실패했습니다"도 아닙니다. 서버가 이미 적용했을 수 있으므로,
 * 사용자에게 다시 확인하라고 말하는 것이 유일하게 정직한 답입니다.
 */
export const CHANGE_UNKNOWN_MESSAGE =
  "요청은 전달되었지만 결과를 확인하지 못했습니다. 취소된 것이 아닙니다 — 아래에서 작업 상태를 확인하세요.";

/** 결과 불명을 뜻하는 서버 code입니다. UI는 이 값을 실패와 다르게 다룹니다. */
export const CHANGE_UNKNOWN_CODE = "change_result_unknown";

export function changeErrorMessage(error: unknown): string {
  if (error instanceof HttpError) {
    if (error.body.code === CHANGE_UNKNOWN_CODE) return CHANGE_UNKNOWN_MESSAGE;
    const known = fixedTextFor(CHANGE_MESSAGES, error.body.code);
    if (known) return known;
  }
  return CHANGE_FALLBACK_MESSAGE;
}

/** 결과를 모르는 실패인지입니다. 네트워크 실패도 여기 포함됩니다. */
export function isUnknownResult(error: unknown): boolean {
  if (error instanceof HttpError) return error.body.code === CHANGE_UNKNOWN_CODE;
  // HttpError가 아니면 응답 자체를 못 받은 것입니다 — 서버는 이미 적용했을 수 있습니다.
  return true;
}

/** 작업 상태를 화면 문구로 옮깁니다. 모르는 값은 되비추지 않습니다. */
export const OPERATION_STATE_TEXT: Record<string, string> = {
  succeeded: "완료되었습니다.",
  failed: "적용되지 않았습니다.",
  unconfirmed: "결과를 확인하지 못했습니다. 목록을 새로고침해 현재 상태를 확인하세요.",
};

export const OPERATION_UNKNOWN_STATE_TEXT = "서버가 이 화면이 모르는 상태로 답했습니다.";
